import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { AgentChat } from '@/components/agent-chat/AgentChat';
import { AgentChatV2ThreadsPanel } from '@/components/finch-chat/FinchChatThreadsPanel';
import { isAgentV2EnabledForUser } from '@/lib/featureFlags';
import { useGetAgentChatWorkspaceQuery } from '@/services/api/agentChatWorkspaceService';
import { selectUser } from '@/state/selectors/root-selectors';

import { ProofreadingView } from './proofreading/ProofreadingView';
import { type ChatVersion, WorkspaceChatHeader } from './WorkspaceChatHeader';

interface WorkspaceChatProps {
  workspaceId: string;
  matterId: string;
  documentId?: string;
  exampleDemandLetterId?: string;
}

export const WorkspaceChat = ({
  workspaceId,
  matterId,
  documentId,
  exampleDemandLetterId,
}: WorkspaceChatProps) => {
  const [mode, setMode] = useState<'chat' | 'proofreading'>('chat');
  const [chatVersion, setChatVersion] = useState<ChatVersion>('agent');
  const user = useSelector(selectUser);
  const isAgentV2Enabled = isAgentV2EnabledForUser(user?.email);

  // Get workspace to access document_id
  const { data: workspace } = useGetAgentChatWorkspaceQuery(
    { matterId, workspaceId },
    { skip: !workspaceId },
  );

  // Broadcast mode changes so the editor can toggle export gating
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const evt =
      mode === 'proofreading' ? 'proofreading:open' : 'proofreading:close';
    window.dispatchEvent(new CustomEvent(evt));
  }, [mode]);

  const header = (
    <div className="sticky top-0 border-b h-12 shadow-md">
      <WorkspaceChatHeader
        matterId={matterId}
        workspaceId={workspaceId}
        documentId={documentId}
        exampleDemandLetterId={exampleDemandLetterId}
        mode={mode}
        onChangeMode={setMode}
        chatVersion={chatVersion}
        onChangeChatVersion={setChatVersion}
        showVersionToggle={isAgentV2Enabled}
        className=""
      />
    </div>
  );

  const proofreadingPanel = (
    <div
      className={
        mode === 'proofreading' ? 'h-full overflow-y-auto' : 'hidden'
      }
    >
      <ProofreadingView workspaceId={workspaceId} matterId={matterId} />
    </div>
  );

  if (chatVersion === 'agent_v2') {
    return (
      <div className="relative z-0 flex flex-col h-full w-full min-h-0">
        {header}
        {/* Workspace Toolbar - only show in chat mode */}
        {mode === 'chat' && workspace?.document_id && null}
        <div
          className={mode === 'chat' ? 'h-full overflow-y-auto' : 'hidden'}
        >
          <AgentChatV2ThreadsPanel matterId={matterId} workspaceId={workspaceId} />
        </div>
        {proofreadingPanel}
      </div>
    );
  }

  return (
    <AgentChat matterId={matterId} workspaceId={workspaceId}>
      <div className="relative z-0 flex flex-col h-full w-full min-h-0">
        {header}
        {/* Workspace Toolbar - only show in chat mode */}
        {mode === 'chat' && workspace?.document_id && null}
        <div className={mode === 'chat' ? 'h-full overflow-y-auto' : 'hidden'}>
          <AgentChat.Content />
        </div>
        {proofreadingPanel}
      </div>
    </AgentChat>
  );
};
